import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import './GenerateVideo.css';
import ProductionWorkflow from './components/ProductionWorkflow';
import WorkflowGeneration from './components/WorkflowGeneration';
import SoraGeneration from './components/SoraGeneration';
import apiService from '../../services/api';

const GenerateVideo = () => {
  const location = useLocation();
  const breadcrumbItems = useMemo(() => [
    { label: '首页', path: '/dashboard' },
    { label: '生成视频', path: null },
  ], []);

  const [activeTab, setActiveTab] = useState(location.state?.tab || 'production');
  const [formData, setFormData] = useState({
    digitalHuman: { type: 'existing', selectedHuman: '', virtualmanKey: '' },
    voice: { type: 'existing', selectedVoice: '', audioUrl: '', script: '', title: '', emotion: undefined, speed: undefined, volume: undefined },
    materials: { type: 'url', files: [], urls: [] },
    bgMusic: { type: 'url', selectedMusic: '', audioUrl: '', uploadedFile: null, uploadError: null },
    sora: { uploadedFile: null, uploadError: null, description: '', ratio: '9:16', duration: '15', model: 'tongyiwanxiang' }
  });
  const [submitting, setSubmitting] = useState(false);
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const [taskId, setTaskId] = useState('');
  const [statusText, setStatusText] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [error, setError] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Load selected avatar and voice from localStorage on component mount
  useEffect(() => {
    const savedAvatar = localStorage.getItem('selectedAvatar');
    const savedVoice = localStorage.getItem('selectedVoice');
    
    if (savedAvatar) {
      try {
        const avatar = JSON.parse(savedAvatar);
        setSelectedAvatar(avatar);
        setFormData(prev => ({
          ...prev,
          digitalHuman: {
            ...prev.digitalHuman,
            selectedHuman: avatar.id,
            virtualmanKey: avatar.key
          }
        }));
      } catch (error) {
        console.error('Error parsing saved avatar:', error);
      }
    }
    
    if (savedVoice) {
      try {
        const voice = JSON.parse(savedVoice);
        setSelectedVoice(voice);
        setFormData(prev => ({
          ...prev,
          voice: {
            ...prev.voice,
            selectedVoice: voice.id,
            audioUrl: voice.sampleUrl || ''
          }
        }));
      } catch (error) {
        console.error('Error parsing saved voice:', error);
      }
    }
  }, []);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setError('');
    setTaskId('');
    setStatusText('');
    setMediaUrl('');
    setTaskSubmitted(false);
  }, []);

  const updateFormData = useCallback((step, data) => {
    setFormData(prev => ({
      ...prev,
      [step]: { ...prev[step], ...data }
    }));
  }, []);

  const handleClearAvatar = useCallback(() => {
    localStorage.removeItem('selectedAvatar');
    setSelectedAvatar(null);
    setFormData(prev => ({
      ...prev,
      digitalHuman: { ...prev.digitalHuman, selectedHuman: '', virtualmanKey: '' }
    }));
  }, []);

  const handleClearVoice = useCallback(() => {
    localStorage.removeItem('selectedVoice');
    setSelectedVoice(null);
    setFormData(prev => ({
      ...prev,
      voice: { ...prev.voice, selectedVoice: '', audioUrl: '' }
    }));
  }, []);

  const isProductionValid = () => {
    // 数字人：选择数字人 或 直接输入VirtualmanKey 二选一
    const digitalHumanValid = formData.digitalHuman.selectedHuman || formData.digitalHuman.virtualmanKey;
    // 声音：(选择声音 + 文案) 或 音频链接 二选一
    const voiceValid = (formData.voice.selectedVoice && formData.voice.script.trim().length > 0) || formData.voice.audioUrl;
    return digitalHumanValid && voiceValid;
  };

  const isWorkflowValid = () => {
    // 数字人：选择数字人 或 直接输入VirtualmanKey 二选一
    const digitalHumanValid = formData.digitalHuman.selectedHuman || formData.digitalHuman.virtualmanKey;
    // 声音：(选择声音 + 文案) 或 音频链接 二选一
    const voiceValid = (formData.voice.selectedVoice && formData.voice.script.trim().length > 0) || formData.voice.audioUrl;
    const materialsValid =
      formData.materials.type === 'url' && formData.materials.urls.length > 0;
    const bgMusicValid =
      formData.bgMusic.type === 'url' && !!formData.bgMusic.audioUrl;
    return digitalHumanValid && voiceValid && materialsValid && bgMusicValid;
  };

  const isSoraValid = () => {
    // SORA视频生成需要：视频描述（必填）
    const descriptionValid = formData.sora.description && formData.sora.description.trim().length > 0;
    return descriptionValid;
  };

  const canGenerate = activeTab === 'production' ? isProductionValid() : 
                     activeTab === 'workflow' ? isWorkflowValid() : 
                     activeTab === 'sora' ? isSoraValid() : false;
  const isButtonDisabled = !canGenerate || submitting || taskSubmitted;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    
    try {
      setSubmitting(true);
      setError('');
      setMediaUrl('');
      setStatusText('');
      setTaskId('');
      
      // Prepare video generation data
      // 数字人Key：优先使用直接输入的virtualmanKey，否则使用选择的数字人
      const virtualmanKey = formData.digitalHuman.virtualmanKey || formData.digitalHuman.selectedHuman;
      // 音色Key：使用选择的声音
      const timbreKey = formData.voice.selectedVoice || '';
      // 音频链接：使用高级选项中的音频链接
      const inputAudioUrl = formData.voice.audioUrl || '';

      if (activeTab === 'workflow') {
        if (formData.materials.type === 'upload') {
          throw new Error('暂不支持直接上传素材，请改用素材URL');
        }

        const materials =
          formData.materials.type === 'url'
            ? formData.materials.urls.filter((item) => item && item.trim())
            : [];

        if (!materials.length) {
          throw new Error('请至少提供一个素材视频链接');
        }

        if (formData.bgMusic.type === 'upload') {
          throw new Error('暂不支持直接上传背景音乐，请使用音频链接');
        }

        let bgmUrl = '';
        if (formData.bgMusic.type === 'url') {
          bgmUrl = formData.bgMusic.audioUrl?.trim() || '';
        }

        if (!bgmUrl) {
          throw new Error('请提供背景音乐的音频链接');
        }

        const workflowPayload = {
          workflowId: process.env.REACT_APP_COZE_WORKFLOW_ID || '',
          parameters: {
            app_key: process.env.REACT_APP_COZE_APP_KEY || '',
            bgm: bgmUrl,
            timbre_key: timbreKey,
            virtualman_key: virtualmanKey,
            wenan: formData.voice.script,
            sucai: materials,
          },
        };

        if (inputAudioUrl) {
          workflowPayload.parameters.audio_url = inputAudioUrl;
        }

        const workflowResult = await apiService.runWorkflow(workflowPayload);

        if (workflowResult?.success === false) {
          throw new Error(workflowResult?.message || '工作流触发失败');
        }

      if (workflowResult?.taskId) {
        setTaskId(workflowResult.taskId);
        setTaskSubmitted(true);
      }

      setStatusText(
        workflowResult?.message || '混剪工作流已提交，请稍后在任务中心查看进度'
      );

        if (workflowResult?.output) {
          setMediaUrl(workflowResult.output);
        } else if (workflowResult?.mediaUrl) {
          setMediaUrl(workflowResult.mediaUrl);
        }

        return;
      }

      if (activeTab === 'sora') {
        // SORA视频生成逻辑
        const soraPayload = {
          Prompt: formData.sora.description,
          AspectRatio: formData.sora.ratio || '9:16',
          Duration: parseInt(formData.sora.duration) || 15
        };

        // 根据选择的模型设置 Provider 和 Model 参数
        const selectedModel = formData.sora.model || 'tongyiwanxiang';
        
        if (selectedModel === 'tongyiwanxiang') {
          // 自研模型
          soraPayload.Provider = 'tongyiwanxiang';
        } else if (selectedModel === 'sora') {
          // Sora 模型
          soraPayload.Provider = 'sora';
          soraPayload.Model = 'sora-2-vip';
        } else if (selectedModel === 'sora-pro') {
          // Sora-Pro 模型
          soraPayload.Provider = 'sora';
          soraPayload.Model = 'sora-2-pro';
        }

        // 如果有上传的图片，添加ImageUrl
        if (formData.sora.uploadedFile && formData.sora.uploadedFile.uploadedId) {
          soraPayload.ImageUrl = formData.sora.uploadedFile.uploadedId;
        }

        console.log('Generating SORA video with data:', soraPayload);
        
        try {
          const result = await apiService.generateSoraVideo(soraPayload);
          
          if (result?.taskId) {
            setTaskId(result.taskId);
            setTaskSubmitted(true);
            setStatusText('SORA视频生成中...');
            pollStatus(result.taskId);
          } else if (result?.id) {
            setTaskId(result.id);
            setTaskSubmitted(true);
            setStatusText('SORA视频生成中...');
            pollStatus(result.id);
          } else {
            setStatusText(result?.message || 'SORA视频生成任务已提交，请稍后在任务中心查看进度');
            if (result?.output || result?.mediaUrl) {
              setMediaUrl(result.output || result.mediaUrl);
            }
          }
        } catch (error) {
          throw new Error(error.message || 'SORA视频生成失败');
        }
        
        return;
      }

      const videoData = {
        VirtualmanKey: virtualmanKey,
        TimbreKey: timbreKey,
        InputAudioUrl: inputAudioUrl,
        Text: formData.voice.script
      };

      // 添加 Title（如果提供）
      if (formData.voice.title && formData.voice.title.trim()) {
        videoData.Title = formData.voice.title.trim();
      }

      // 添加 AudioParam 如果有情绪、速度或音量参数
      if (formData.voice.emotion || formData.voice.speed || formData.voice.volume) {
        videoData.AudioParam = {};
        if (formData.voice.emotion) {
          videoData.AudioParam.Emotion = formData.voice.emotion;
        }
        if (formData.voice.speed) {
          videoData.AudioParam.Speed = formData.voice.speed;
        }
        if (formData.voice.volume) {
          videoData.AudioParam.Volume = formData.voice.volume;
        }
      }

      console.log('Generating video with data:', videoData);

      const result = await apiService.generateVideo(videoData);
      
      if (result?.taskId) {
        setTaskId(result.taskId);
        setTaskSubmitted(true);
        setStatusText('视频生成中...');
        pollStatus(result.taskId);
      } else {
        throw new Error(result?.message || '生成任务创建失败');
      }
    } catch (e) {
      setError(e.message || '生成失败');
    } finally {
      setSubmitting(false);
    }
  };

  const pollStatus = async (tid) => {
    let attempts = 0;
    const maxAttempts = 60; // ~5 minutes if every 5s
    const intervalMs = 5000;
    
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const result = await apiService.getVideoStatus(tid);
        
        // Update status text
        if (result?.statusText) {
          setStatusText(result.statusText);
        }
        
        // Update media URL if available
        if (result?.mediaUrl) {
          setMediaUrl(result.mediaUrl);
        }
        
        // Check if generation is complete
        if (result?.status === 'completed' || result?.status === 4 || result?.mediaUrl) {
          clearInterval(timer);
          setStatusText('视频生成完成！');
          setTaskSubmitted(false); // Re-enable button when task completes
        } else if (result?.status === 'failed' || result?.status === -1) {
          clearInterval(timer);
          setError('视频生成失败');
          setTaskSubmitted(false); // Re-enable button when task fails
        }
      } catch (e) {
        console.error('Error polling status:', e);
        // Stop polling on repeated errors
        if (attempts > 3) {
          clearInterval(timer);
          setError('无法获取生成状态');
        }
      }
      
      // Stop polling after max attempts
      if (attempts >= maxAttempts) {
        clearInterval(timer);
        setStatusText('请稍后直接在“我的视频”中查看结果');
        setTaskSubmitted(false); // Re-enable button when polling times out
      }
    }, intervalMs);
  };

  return (
    <div className="generate-video-page">
      <Breadcrumb items={breadcrumbItems} />
      <h1 className="page-title">生成视频</h1>

      <div className="content-card">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'production' ? 'active' : ''}`} 
            type="button" 
            onClick={() => handleTabChange('production')}
          >
            生成数字人视频
          </button>
          <button
            className={`tab ${activeTab === 'workflow' ? 'active' : ''}`}
            type="button"
            onClick={() => handleTabChange('workflow')}
          >
            生成混剪工作流视频
          </button>
          <button
            className={`tab ${activeTab === 'sora' ? 'active' : ''}`}
            type="button"
            onClick={() => handleTabChange('sora')}
          >
            文生视频
          </button>
        </div>

        {activeTab === 'production' && (
          <ProductionWorkflow
            data={formData}
            onChange={updateFormData}
            onGenerate={handleGenerate}
            isGenerating={submitting}
            isValid={isProductionValid()}
            isButtonDisabled={isButtonDisabled}
            selectedAvatar={selectedAvatar}
            selectedVoice={selectedVoice}
            onClearAvatar={handleClearAvatar}
            onClearVoice={handleClearVoice}
          />
        )}

        {activeTab === 'workflow' && (
          <WorkflowGeneration
            data={formData}
            onChange={updateFormData}
            onGenerate={handleGenerate}
            isGenerating={submitting}
            isValid={isWorkflowValid()}
            isButtonDisabled={isButtonDisabled}
            selectedAvatar={selectedAvatar}
            selectedVoice={selectedVoice}
          />
        )}

        {activeTab === 'sora' && (
          <SoraGeneration
            data={formData}
            onChange={updateFormData}
            onGenerate={handleGenerate}
            isGenerating={submitting}
            isValid={isSoraValid()}
            isButtonDisabled={isButtonDisabled}
          />
        )}

        {/* Status and Results Section */}
        {(error || taskId || statusText || mediaUrl) && (
          <div className="gv-status">
            {error && (
              <div className="gv-status-error">{error}</div>
            )}
            {taskId && (
              <div className="gv-status-info">任务ID：{taskId}（自动刷新状态）</div>
            )}
            {statusText && (
              <div className="gv-status-info">状态：{statusText}</div>
            )}
            {mediaUrl && (
              <a className="gv-status-link" href={mediaUrl} target="_blank" rel="noreferrer">
                查看生成结果 →
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateVideo;

